import { IMPORT_SLICE, ImportReport } from '@itc/shared';
import { z } from 'zod';
import { AGREEMENTS, COOKIE_VALUE, JOIN_OUTCOMES, LINK_STATES, ORG_STATUSES, SHOWS, SYNC_KINDS, SYNC_STATUSES, TOKEN_STATUSES } from './rules.ts';
import { MAX_BAN_REASON, MAX_REASON, ORG_TONES } from './tools.ts';

// Members' shapes (plan 5B), used by the server and the browser. There is one per
// audience (design §6.12): `members.view` gets `view`, `members.manage` gets `manage`, and
// a field that tier may not see is never in the first. The `tier` says which it is.

export const MemberId = z.string().regex(/^[a-z0-9]{15}$/, 'must be a record id');

/** What everyone with members.view sees. */
const Common = z.object({
  id: z.string(),
  /** The RSI handle, or empty for a row that only has a Discord id. */
  handle: z.string(),
  /** Whether a link is set, proven or carried over. */
  linked: z.boolean(),
  /** `Linked`, `Linked before migration` or `Not linked`. */
  linkLabel: z.string(),
  orgStatus: z.enum(ORG_STATUSES),
  /** A coarse flag: a threat was found, or they're banned. Why is for members.manage. */
  threat: z.boolean(),
});

export const MemberOrg = z.object({ symbol: z.string(), name: z.string(), activities: z.array(z.string()) });
export type MemberOrg = z.infer<typeof MemberOrg>;

const ViewTier = Common.extend({ tier: z.literal('view') });

/** A roster row for members.manage: what to scan for. */
const ManageRowTier = Common.extend({
  tier: z.literal('manage'),
  permanentThreat: z.boolean(),
  reasons: z.array(z.string()),
  hostileOrgs: z.array(z.string()),
});

/** One profile for members.manage. */
const ManageDetailTier = ManageRowTier.extend({
  bio: z.string(),
  orgs: z.array(MemberOrg),
  /** Every org they've been seen in, upper-case. It only grows. */
  history: z.array(z.string()),
  redactedOrgCount: z.number().int(),
  banReason: z.string(),
  agreement: z.enum(AGREEMENTS),
  /** The link label's machine form, for the edit form. A viewer only gets the label. */
  linkState: z.enum(LINK_STATES),
  /** The flag itself, apart from a ban: `threat` is either. */
  isThreat: z.boolean(),
  /** Hidden from the org on RSI (ITCBot's "ITC hidden"). */
  hidden: z.boolean(),
  evasive: z.boolean(),
});

export const MemberRow = z.discriminatedUnion('tier', [ViewTier, ManageRowTier]);
export type MemberRow = z.infer<typeof MemberRow>;

export const MemberDetail = z.discriminatedUnion('tier', [ViewTier, ManageDetailTier]);
export type MemberDetail = z.infer<typeof MemberDetail>;

export const ListQuery = z.object({
  q: z.string().trim().max(100).default(''),
  show: z.enum(SHOWS).default('all'),
  /** Opaque to the browser: the next page. */
  cursor: z.string().regex(/^[0-9]{1,6}$/).optional(),
});
export type ListQuery = z.input<typeof ListQuery>;

export const MemberPage = z.object({
  items: z.array(MemberRow),
  nextCursor: z.string().nullable(),
  /** How many rows the filters match, over every page. */
  matched: z.number().int(),
  /** How many rows each filter would show, ignoring the search. */
  counts: z.record(z.enum(SHOWS), z.number().int()),
});
export type MemberPage = z.infer<typeof MemberPage>;

/**
 * How the RSI account's cookies stand. The cookies themselves are never in it, nor
 * anything made from them: only whether they are set, the account's handle and the status.
 */
export const RsiStatus = z.object({
  /** Whether ITC_SECRETS_KEY is set. Without it the cookies can't be kept and logged-in calls don't run. */
  secretsAvailable: z.boolean(),
  set: z.boolean(),
  /** The account the cookies belong to, once a test has passed. */
  handle: z.string(),
  status: z.enum(TOKEN_STATUSES),
  /** When a test or a failed call last settled it, or null. */
  checkedAt: z.iso.datetime().nullable(),
});
export type RsiStatus = z.infer<typeof RsiStatus>;

const Cookie = z.string().trim().regex(COOKIE_VALUE, 'must be the cookie’s value alone, 8 to 512 characters, with no spaces or semicolons');

/** The two cookies ITCBot uses: `Rsi-Token` and `_rsi_device`, not `_device_id`. */
export const RsiCredentialsInput = z.object({ token: Cookie, device: Cookie });
export type RsiCredentialsInput = z.infer<typeof RsiCredentialsInput>;

/** The state after a test, and what it said in words. */
export const RsiTest = z.object({ status: RsiStatus, message: z.string() });
export type RsiTest = z.infer<typeof RsiTest>;

// --- /join ------------------------------------------------------------------------------

export const JoinStartInput = z.object({ handle: z.string().trim().min(1).max(64) });
export type JoinStartInput = z.infer<typeof JoinStartInput>;

export const JoinAgreementInput = z.object({ agreed: z.boolean() });
export type JoinAgreementInput = z.infer<typeof JoinAgreementInput>;

/** Why a step couldn't go on. `retry` is the step to offer again. */
export const JOIN_PROBLEMS = ['bad_handle', 'not_found', 'unreadable', 'orgs_unreadable'] as const;
export type JoinProblem = (typeof JOIN_PROBLEMS)[number];

/**
 * Where /join stands and what to show, for the Discord handlers to put in words. Only the
 * person who asked ever sees one, and only through Discord. None of this reaches a browser.
 */
export const JoinReply = z.discriminatedUnion('step', [
  /** No run under way. `linkedAs` is the handle already linked to them, and `link` whether it was proven here. */
  z.object({ step: z.literal('intro'), linkedAs: z.string(), link: z.enum(LINK_STATES) }),
  /** Waiting for the code in their bio. `notice` says what just happened. */
  z.object({ step: z.literal('code'), handle: z.string(), code: z.string(), notice: z.enum(['', 'code_missing', 'regenerated']) }),
  /** Hidden orgs to show, then check again. `again` is true when a check found them still hidden. `prompt` is the `redactedPrompt` setting: the worker has no settings. */
  z.object({ step: z.literal('unredact'), handle: z.string(), redacted: z.number().int(), again: z.boolean(), prompt: z.string().max(2000).optional() }),
  /**
   * The anti-piracy agreement. `threats` are the orgs and activities behind it, and may be empty. `prompt` is the `antiPiracyAgreement` setting.
   * `lead` is the `confirmPiracyMessage` setting filled in for those orgs: it is left out when `threats` is empty.
   */
  z.object({
    step: z.literal('agreement'),
    handle: z.string(),
    threats: z.array(z.object({ symbol: z.string(), activities: z.array(z.string()) })),
    prompt: z.string().max(2000).optional(),
    lead: z.string().max(2000).optional(),
  }),
  /**
   * The run ended. `orgs` are the ones removed, for an evasion, and empty otherwise: what is held against someone stays on their profile.
   * `inOrg` says the roster shows them in the org; for someone who isn't, the closing message tells them to apply, with a link to the org's page (`orgSymbol`).
   * `message` is the closing text from a setting, filled in: `callToActionMessage` for someone who is told to apply, `outcomeCleanMessage` for a clean member.
   * The other outcomes have none.
   */
  z.object({
    step: z.literal('finished'),
    handle: z.string(),
    outcome: z.enum(JOIN_OUTCOMES),
    orgs: z.array(z.string()),
    inOrg: z.boolean(),
    orgSymbol: z.string(),
    message: z.string().max(2000).optional(),
  }),
  /** The handle belongs to someone else's proven link. */
  z.object({ step: z.literal('refused'), handle: z.string() }),
  /** No session: it timed out, or the run had already ended. `message` is the `sessionTimeoutMessage` setting. */
  z.object({ step: z.literal('expired'), message: z.string().max(2000).optional() }),
  z.object({ step: z.literal('problem'), problem: z.enum(JOIN_PROBLEMS), handle: z.string(), retry: z.enum(['start', 'verify', 'recheck']) }),
]);
export type JoinReply = z.infer<typeof JoinReply>;

/** The admin's Join button, posted to a channel. */
export const JoinPosted = z.object({ posted: z.literal(true) });
export type JoinPosted = z.infer<typeof JoinPosted>;

/** How one kind of sync pass stands. */
export const PassStatus = z.object({
  kind: z.enum(SYNC_KINDS),
  state: z.enum(SYNC_STATUSES),
  /** Whether this server is working on it now. A pass can be `running` and idle between runs, or after a restart. */
  active: z.boolean(),
  trigger: z.enum(['', 'job', 'manual']),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  /** When a pass last finished well. */
  lastOkAt: z.iso.datetime().nullable(),
  /** Numbers by name: a roster pass has `rows`, `created`, `updated`, `left`; a public one `total`, `scraped`, `failed`, `changed`, `moved`. */
  counts: z.record(z.string(), z.number()),
  lastError: z.string(),
});
export type PassStatus = z.infer<typeof PassStatus>;

/** Both passes, and whether they may run. */
export const SyncOverview = z.object({
  /** The `syncEnabled` setting: whether the jobs may reach RSI. */
  enabled: z.boolean(),
  /** Whether the roster can be read now: sealed secrets are on and the cookies have passed a test. */
  rosterReady: z.boolean(),
  /** Seconds until RSI calls resume after a 429, or 0. */
  pausedForSeconds: z.number().int(),
  roster: PassStatus,
  public: PassStatus,
  /** How many snapshots are kept, and the newest. */
  snapshots: z.object({ count: z.number().int(), latestAt: z.iso.datetime().nullable() }),
});
export type SyncOverview = z.infer<typeof SyncOverview>;

// --- The review queue and the tools (Chunk 6) -------------------------------------------

export const ReviewQuery = z.object({
  q: z.string().trim().max(100).default(''),
  /** Opaque to the browser: the next page. */
  cursor: z.string().regex(/^[0-9]{1,6}$/).optional(),
});
export type ReviewQuery = z.input<typeof ReviewQuery>;

/** An org's symbol and how to colour it. */
export const ReviewOrg = z.object({ symbol: z.string(), tone: z.enum(ORG_TONES) });
export type ReviewOrg = z.infer<typeof ReviewOrg>;

/** One flagged profile, with everything the console's review row shows. For members.manage only. */
export const ReviewItem = z.object({
  id: z.string(),
  handle: z.string(),
  linkLabel: z.string(),
  orgStatus: z.enum(ORG_STATUSES),
  permanentThreat: z.boolean(),
  evasive: z.boolean(),
  /** Hidden from the org on RSI. */
  hidden: z.boolean(),
  redactedOrgCount: z.number().int(),
  banReason: z.string(),
  reasons: z.array(z.string()),
  hostileOrgs: z.array(z.string()),
  current: z.array(ReviewOrg),
  history: z.array(ReviewOrg),
});
export type ReviewItem = z.infer<typeof ReviewItem>;

export const ReviewPage = z.object({
  items: z.array(ReviewItem),
  nextCursor: z.string().nullable(),
  /** How many profiles are held, matching the search. */
  matched: z.number().int(),
});
export type ReviewPage = z.infer<typeof ReviewPage>;

/** What a re-scrape did: the profile as it is now, which fields changed, and whether the organizations page was read. */
export const RescrapeResult = z.object({ member: MemberDetail, changed: z.array(z.string()), orgsRead: z.boolean() });
export type RescrapeResult = z.infer<typeof RescrapeResult>;

/** A new bio code, for the manager to pass on. It is shown once and not kept anywhere but the profile. */
export const BiocodeResult = z.object({ code: z.string() });
export type BiocodeResult = z.infer<typeof BiocodeResult>;

const Reason = z.string().trim().min(1, 'A reason is required').max(MAX_REASON, `A reason is at most ${String(MAX_REASON)} characters`);

export const PardonInput = z.object({ reason: Reason });
export type PardonInput = z.infer<typeof PardonInput>;

/** One named edit, with the reason for it. */
export const EditInput = z.discriminatedUnion('edit', [
  z.object({ edit: z.literal('link_state'), value: z.enum(LINK_STATES), reason: Reason }),
  z.object({ edit: z.literal('is_threat'), value: z.boolean(), reason: Reason }),
  z.object({ edit: z.literal('permanent_threat'), value: z.boolean(), reason: Reason }),
  z.object({ edit: z.literal('agreement'), value: z.enum(AGREEMENTS), reason: Reason }),
  z.object({ edit: z.literal('ban_reason'), value: z.string().max(MAX_BAN_REASON), reason: Reason }),
]);
export type EditInput = z.infer<typeof EditInput>;

/** The handle typed to confirm (the profile's id, if it has none). */
export const PurgeInput = z.object({ confirm: z.string().trim().min(1).max(64) });
export type PurgeInput = z.infer<typeof PurgeInput>;

export const Purged = z.object({ purged: z.literal(true) });
export type Purged = z.infer<typeof Purged>;

// --- The import (Chunk 7, R7, ADR 0019) -------------------------------------------------------

/**
 * One row of ITCBot's `members`. Loose on purpose, as ADR 0019 says: the old schema was
 * never written down, so a row that is wrong is refused and listed (R7), not a failed request.
 * Only these fields leave the browser: Fleet's `fc_*` stay behind for phase 6.
 */
export const MemberImportRow = z.object({
  id: z.string().max(32),
  created: z.string().max(40),
  discord_id: z.unknown(),
  rsi_handle: z.unknown(),
  biocode: z.unknown(),
  rsi_bio: z.unknown(),
  linked: z.unknown(),
  rsi_itc_hidden: z.unknown(),
  rsi_itc_member_type: z.unknown(),
  rsi_itc_member_roles: z.unknown(),
  rsi_current_orgs: z.unknown(),
  rsi_org_history: z.unknown(),
  rsi_redacted_org_count: z.unknown(),
  evasive: z.unknown(),
  antipiracy: z.unknown(),
  onboard_complete: z.unknown(),
  outreach_data: z.unknown(),
  banned: z.unknown(),
  threat_reasons: z.unknown(),
  hostile_orgs_found: z.unknown(),
  ban_reason: z.unknown(),
});
export type MemberImportRow = z.infer<typeof MemberImportRow>;

/** One row of ITCBot's `organizations`. */
export const OrgImportRow = z.object({
  id: z.string().max(32),
  created: z.string().max(40),
  rsi_org_symbol: z.unknown(),
  rsi_org_activities: z.unknown(),
  tag: z.unknown(),
  notes: z.unknown(),
  name: z.unknown(),
});
export type OrgImportRow = z.infer<typeof OrgImportRow>;

/** An earlier slice's row that a dry run would create, so this slice can clash with it. Refused for an import. */
const ImportClaim = z.object({ id: z.string().max(32), keys: z.array(z.object({ kind: z.string().max(20), value: z.string().max(64) })).max(3) });

const importInput = <Row extends z.ZodType>(row: Row) =>
  z.object({
    dryRun: z.boolean(),
    rows: z.array(row).min(1).max(IMPORT_SLICE),
    earlier: z.array(ImportClaim).max(500).optional(),
  });
export const MemberImportInput = importInput(MemberImportRow);
export const OrgImportInput = importInput(OrgImportRow);
export type MemberImportInput = z.infer<typeof MemberImportInput>;
export type OrgImportInput = z.infer<typeof OrgImportInput>;

/** ADR 0019's report, and the ids of the rows it created (or, for a dry run, would). */
export const MembersImportReport = ImportReport.extend({ creating: z.array(z.string()) });
export type MembersImportReport = z.infer<typeof MembersImportReport>;
